import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseInactivityTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
}

interface UseInactivityTimeoutResult {
  showWarning: boolean;
  minutesRemaining: number;
  resetTimer: () => void;
}

const useInactivityTimeout = ({
  timeoutMinutes = 30,
  warningMinutes = 5,
}: UseInactivityTimeoutOptions = {}): UseInactivityTimeoutResult => {
  const [showWarning, setShowWarning] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(warningMinutes);
  const warningStartRef = useRef<number | null>(null);
  const warningIntervalRef = useRef<number | null>(null);
  const warningTimeoutRef = useRef<number | null>(null);
  const logoutTimeoutRef = useRef<number | null>(null);

  const warningStartMs = useMemo(
    () => Math.max(timeoutMinutes - warningMinutes, 0) * 60 * 1000,
    [timeoutMinutes, warningMinutes],
  );
  const totalTimeoutMs = useMemo(
    () => Math.max(timeoutMinutes, 0) * 60 * 1000,
    [timeoutMinutes],
  );

  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      window.clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (warningIntervalRef.current) {
      window.clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();
    warningStartRef.current = null;
    setShowWarning(false);
    setMinutesRemaining(warningMinutes);

    warningTimeoutRef.current = window.setTimeout(() => {
      warningStartRef.current = Date.now();
      setShowWarning(true);
      setMinutesRemaining(warningMinutes);

      warningIntervalRef.current = window.setInterval(() => {
        if (!warningStartRef.current) return;
        const elapsed = Date.now() - warningStartRef.current;
        const remainingMs = Math.max(warningMinutes * 60 * 1000 - elapsed, 0);
        setMinutesRemaining(Math.ceil(remainingMs / 60000));
      }, 1000);
    }, warningStartMs);

    logoutTimeoutRef.current = window.setTimeout(() => {
      window.dispatchEvent(new Event('session-inactivity-timeout'));
    }, totalTimeoutMs);
  }, [clearTimers, warningMinutes, warningStartMs, totalTimeoutMs]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    const onActivity = () => resetTimer();

    resetTimer();
    events.forEach((event) => window.addEventListener(event, onActivity));

    return () => {
      clearTimers();
      events.forEach((event) => window.removeEventListener(event, onActivity));
    };
  }, [clearTimers, resetTimer]);

  return { showWarning, minutesRemaining, resetTimer };
};

export default useInactivityTimeout;
